// src/components/AddAppointmentScreen.jsx
// Novo/Editar agendamento com autocomplete, recorrência em lote, toast e UI premium responsiva.
// Rotas esperadas:
// - /schedule/new
// - /schedule/edit  state { appointment }
// Ao salvar: volta para /schedule com refresh.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  LoaderCircle,
  MapPin,
  Menu,
  Repeat2,
  Save,
  Search,
  Video,
  X,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/addAppointment.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const SESSION_TYPES = [
  {
    value: "Presencial",
    label: "Presencial",
    icon: <MapPin size={20} aria-hidden="true" />,
    description: "Atendimento realizado presencialmente.",
    tone: "green",
  },
  {
    value: "Online",
    label: "Online",
    icon: <Video size={20} aria-hidden="true" />,
    description: "Atendimento remoto por videochamada.",
    tone: "blue",
  },
];

const RECURRENCE_OPTIONS = [
  { value: "Nenhuma", label: "Nenhuma", description: "Criar apenas este horário." },
  { value: "Semanal", label: "Semanal", description: "Repetir a cada 7 dias." },
  { value: "Quinzenal", label: "Quinzenal", description: "Repetir a cada 2 semanas." },
  { value: "Mensal", label: "Mensal", description: "Repetir mensalmente." },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

async function readApiPayload(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const data = await response.json();
      return data && typeof data === "object"
        ? data
        : { message: String(data || `Erro HTTP ${response.status}`) };
    }

    const text = await response.text();
    return { message: text || `Erro HTTP ${response.status}` };
  } catch {
    return { message: `Erro HTTP ${response.status}` };
  }
}



async function fetchGoogleCalendarStatus(token) {
  if (!token) {
    return { conectado: false };
  }

  try {
    const response = await fetch(
      `${API_BASE}/psy/integracoes/google/status`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      return { conectado: false };
    }

    const data = await readApiPayload(response);

    return {
      ...data,
      conectado: Boolean(data?.conectado),
    };
  } catch {
    return { conectado: false };
  }
}
function getApiMessage(payload, fallback = "Não foi possível concluir a operação.") {
  if (!payload) return fallback;

  if (typeof payload === "string") {
    return payload || fallback;
  }

  return payload.message || payload.error || fallback;
}

function formatApiConflictItem(conflict) {
  if (!conflict) return "";

  const existing = conflict.agendamentoExistente || conflict.existente || {};
  const data = conflict.data || existing.data || "";
  const hora = conflict.hora || existing.hora || "";
  const clienteNome =
    existing.clienteNome ||
    existing.nomeCliente ||
    existing.cliente?.nome ||
    conflict.clienteNome ||
    "agendamento existente";

  const servico = existing.servico || conflict.servico || "";
  const status = existing.status || conflict.status || "";
  const partes = [];

  if (data || hora) {
    partes.push(`${data || "Data não informada"}${hora ? ` às ${hora}` : ""}`);
  }

  partes.push(`já existe ${clienteNome}`);

  if (servico) {
    partes.push(servico);
  }

  if (status) {
    partes.push(`status: ${status}`);
  }

  return partes.join(" • ");
}

function getConflictDetails(payload) {
  const conflicts = Array.isArray(payload?.conflitos) ? payload.conflitos : [];

  return conflicts
    .map(formatApiConflictItem)
    .filter(Boolean)
    .slice(0, 6);
}


function isScheduleConflictResponse(response, payload) {
  if (response?.status !== 409) {
    return false;
  }

  if (payload?.code === "SCHEDULE_TIME_CONFLICT") {
    return true;
  }

  return (
    Array.isArray(payload?.conflitos) &&
    payload.conflitos.length > 0
  );
}

function getScheduleErrorPresentation(response, payload) {
  const isConflict = isScheduleConflictResponse(response, payload);

  if (isConflict) {
    return {
      type: "warning",
      title: "Conflito de horário",
    };
  }

  if (payload?.code === "SCHEDULE_CONFIRMATION_INDEX_ERROR") {
    return {
      type: "error",
      title: "Erro interno no agendamento",
    };
  }

  if (response?.status === 409) {
    return {
      type: "error",
      title: "Não foi possível salvar",
    };
  }

  return {
    type: "error",
    title: "Erro ao salvar",
  };
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

// function normalizeSpaces(value = "") {
//   return String(value || "").replace(/\s+/g, " ").trim();
// }

function normalizeMultiline(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

// Mantém horário local com offset, evitando virar dia por conversão UTC.
function formatISOWithOffset(date) {
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  const ss = "00";

  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  const offsetHour = pad2(Math.floor(abs / 60));
  const offsetMinute = pad2(abs % 60);

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}${sign}${offsetHour}:${offsetMinute}`;
}

function todayISO() {
  const date = new Date();
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function nowRoundedTime() {
  const date = new Date();
  date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15, 0, 0);

  if (date.getMinutes() === 60) {
    date.setHours(date.getHours() + 1, 0, 0, 0);
  }

  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function parseISODate(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);

  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return null;

  const date = new Date(y, mo - 1, d);

  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mo - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }

  return { y, mo, d };
}

function parseTimeHHMM(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{2}):(\d{2})$/);

  if (!match) return null;

  const hh = Number(match[1]);
  const mm = Number(match[2]);

  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;

  return { hh, mm };
}

function buildLocalDateTime(dateISO, timeHHMM) {
  const dateParts = parseISODate(dateISO);
  const timeParts = parseTimeHHMM(timeHHMM);

  if (!dateParts || !timeParts) return null;

  return new Date(
    dateParts.y,
    dateParts.mo - 1,
    dateParts.d,
    timeParts.hh,
    timeParts.mm,
    0,
    0
  );
}

function addDaysLocal(baseDate, amount) {
  const date = new Date(baseDate);

  date.setDate(date.getDate() + Number(amount || 0));

  return date;
}

function addMonthsKeepDay(baseDate, amount) {
  const date = new Date(baseDate);
  const originalDay = date.getDate();

  const target = new Date(
    date.getFullYear(),
    date.getMonth() + amount,
    1,
    date.getHours(),
    date.getMinutes(),
    0,
    0
  );

  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(originalDay, lastDay));

  return target;
}

function parseMoneyBR(value) {
  if (value === null || value === undefined || value === "") return NaN;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }

  const normalized = String(value)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatMoneyFromDigits(value) {
  const digits = onlyDigits(value).slice(0, 10);

  if (!digits) return "";

  const number = Number(digits) / 100;

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoneyForInput(value) {
  const number = parseMoneyBR(value);

  if (!Number.isFinite(number)) return "";

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrencyBR(value) {
  const number = parseMoneyBR(value);

  if (!Number.isFinite(number)) return "R$ 0,00";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function formatDateBR(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "--/--/----";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

// function formatLongDateBR(date) {
//   if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

//   return new Intl.DateTimeFormat("pt-BR", {
//     weekday: "long",
//     day: "2-digit",
//     month: "long",
//   }).format(date);
// }

function formatPhoneBR(value) {
  const digits = onlyDigits(value);

  if (!digits) return "";

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  if (digits.length >= 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }

  return value;
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "P";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getClientFromEditingData(editingData) {
  const cliente = editingData?.clienteId;

  if (cliente && typeof cliente === "object" && cliente._id) {
    return {
      _id: cliente._id,
      nome: cliente.nome || "",
      telefone: editingData?.telefone || cliente.telefone || "",
      email: cliente.email || "",
    };
  }

  return null;
}

function Field({ label, required, error, hint, children, className = "" }) {
  return (
    <div className={`app-field ${className} ${error ? "app-field--error" : ""}`}>
      <div className="app-field__top">
        <label className="app-label">
          {label}
          {required ? <span className="app-req">*</span> : null}
        </label>

        {hint ? <span className="app-hint">{hint}</span> : null}
      </div>

      {children}

      {error ? (
        <div className="app-err" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function SessionTypeCard({ option, active, onSelect, disabled }) {
  return (
    <button
      className={`app-choice app-choice--${option.tone} ${
        active ? "app-choice--active" : ""
      }`}
      type="button"
      onClick={() => onSelect(option.value)}
      disabled={disabled}
    >
      <span className="app-choice__icon">{option.icon}</span>
      <span>
        <strong>{option.label}</strong>
        <small>{option.description}</small>
      </span>
    </button>
  );
}

function RecurrenceCard({ option, active, disabled, onSelect }) {
  return (
    <button
      className={`app-recur-card ${active ? "app-recur-card--active" : ""}`}
      type="button"
      onClick={() => onSelect(option.value)}
      disabled={disabled}
    >
      <strong>{option.label}</strong>
      <small>{option.description}</small>
    </button>
  );
}

export default function AddAppointmentScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const pageRef = useRef(null);

  const editingData = location.state?.appointment || null;
  const isEdit = Boolean(editingData && editingData._id);
  const editingRecurrenceId =
    editingData?.recorrenciaId ||
    editingData?.recorrencia?.id ||
    null;
  const isRecurringEdit = Boolean(isEdit && editingRecurrenceId);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seriesScopeOpen, setSeriesScopeOpen] = useState(false);
  const [seriesSelectionOpen, setSeriesSelectionOpen] = useState(false);
  const [seriesItems, setSeriesItems] = useState([]);
  const [selectedSeriesIds, setSelectedSeriesIds] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [errors, setErrors] = useState({});
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    carregando: true,
    conectado: false,
  });


  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [tipoSessao, setTipoSessao] = useState("Presencial");
  const [criarGoogleMeet, setCriarGoogleMeet] = useState(false);
  const [dateISO, setDateISO] = useState("");
  const [hora, setHora] = useState("");
  const [valor, setValor] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [recorrencia, setRecorrencia] = useState("Nenhuma");
  const [repeatCount, setRepeatCount] = useState("1");

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const [openSug, setOpenSug] = useState(false);
  const [searchError, setSearchError] = useState("");

  const sugBoxRef = useRef(null);
  const skipNextSuggestRef = useRef(false);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const selectedDateTime = useMemo(
    () => buildLocalDateTime(dateISO, hora),
    [dateISO, hora]
  );

  const selectedType = useMemo(
    () => SESSION_TYPES.find((item) => item.value === tipoSessao) || SESSION_TYPES[0],
    [tipoSessao]
  );

  const recurrencePreview = useMemo(() => {
    const base = selectedDateTime;

    if (!base) return [];

    const repeat = Math.max(1, Number.parseInt(repeatCount || "1", 10) || 1);
    const safeRepeat = recorrencia === "Nenhuma" ? 1 : Math.min(repeat, 60);
    const dates = [base];

    if (recorrencia === "Nenhuma" || safeRepeat === 1) return dates;

    for (let index = 1; index < safeRepeat; index += 1) {
      if (recorrencia === "Semanal") {
        dates.push(addDaysLocal(base, 7 * index));
      } else if (recorrencia === "Quinzenal") {
        dates.push(addDaysLocal(base, 14 * index));
      } else if (recorrencia === "Mensal") {
        dates.push(addMonthsKeepDay(base, index));
      }
    }

    return dates;
  }, [selectedDateTime, recorrencia, repeatCount]);

  const completion = useMemo(() => {
    const checks = [
      Boolean(clienteSelecionado?._id),
      Boolean(dateISO),
      Boolean(hora),
      Number.isFinite(parseMoneyBR(valor)) && parseMoneyBR(valor) > 0,
      Boolean(tipoSessao),
    ];

    if (recorrencia !== "Nenhuma") {
      checks.push(Number(repeatCount) > 0);
    }

    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [clienteSelecionado, dateISO, hora, valor, tipoSessao, recorrencia, repeatCount]);

  const titulo = isEdit ? "Editar agendamento" : "Novo agendamento";

  useEffect(() => {
    let ativo = true;

    async function carregarGoogleCalendar() {
      const token = readAuth(AUTH_KEYS.TOKEN);
      const status = await fetchGoogleCalendarStatus(token);

      if (ativo) {
        setGoogleCalendarStatus({
          carregando: false,
          conectado: Boolean(status?.conectado),
        });
      }
    }

    carregarGoogleCalendar();

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (tipoSessao !== "Online") {
      setCriarGoogleMeet(false);
    }
  }, [tipoSessao]);


  const showMessage = useCallback(
    (type, text, options = {}) => {
      const tone = ["success", "warning", "error", "info"].includes(type)
        ? type
        : "info";

      const duration =
        options.autoCloseMs === 0
          ? 0
          : Number(options.autoCloseMs || 5200);

      toast[tone]({
        title: options.title || undefined,
        message: text,
        details: Array.isArray(options.details)
          ? options.details
          : [],
        actionLabel: options.actionLabel || "",
        onAction:
          typeof options.onAction === "function"
            ? options.onAction
            : null,
        duration,
      });
    },
    [toast]
  );

  const focusFirstError = () => {
    window.setTimeout(() => {
      const root = pageRef.current || document;
      const errorElement = root.querySelector(".app-err");
      const target =
        errorElement?.closest("label, .app-field, .pr-field, .rev-field, .al-form-card, .al-panel, section") ||
        errorElement;

      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusable = target.querySelector?.(
        "input, textarea, select, button:not([disabled])"
      );

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus({ preventScroll: true });
      }
    }, 80);
  };

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      showMessage("warning", "Sessão expirada. Faça login novamente para continuar.", {
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
      });
      return null;
    }

    return { token, userId };
  }, [navigate, showMessage]);

  useEffect(() => {
    if (isEdit) {
      const selectedClient = getClientFromEditingData(editingData);

      if (selectedClient) {
        setClienteSelecionado(selectedClient);
        skipNextSuggestRef.current = true;
        setQuery(selectedClient.nome || "");
        setOpenSug(false);
        setSuggestions([]);
      }

      setTipoSessao(editingData?.servico || "Presencial");
      setCriarGoogleMeet(
        Boolean(
          editingData?.googleMeet?.solicitado ||
          editingData?.googleMeet?.criado
        )
      );

      const rawDate = editingData?.dataHora || editingData?.data || "";
      const date = new Date(rawDate);

      if (!Number.isNaN(date.getTime())) {
        setDateISO(`${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`);
        setHora(`${pad2(date.getHours())}:${pad2(date.getMinutes())}`);
      } else {
        setDateISO(todayISO());
        setHora(editingData?.hora || nowRoundedTime());
      }

      setValor(formatMoneyForInput(editingData?.valorAgendamento ?? editingData?.valor ?? ""));
      setObservacoes(editingData?.observacoes || "");

      const recurrenceType = String(
        editingData?.recorrenciaTipo ||
          editingData?.recorrencia?.tipo ||
          ""
      ).toLowerCase();

      setRecorrencia(
        recurrenceType === "semanal"
          ? "Semanal"
          : recurrenceType === "quinzenal"
            ? "Quinzenal"
            : recurrenceType === "mensal"
              ? "Mensal"
              : "Nenhuma"
      );
      setRepeatCount("1");

      return;
    }

    setDateISO(todayISO());
    setHora(nowRoundedTime());
  }, [isEdit, editingData]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!sugBoxRef.current) return;
      if (!sugBoxRef.current.contains(event.target)) {
        setOpenSug(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const fetchClients = useCallback(
    async (search) => {
      const auth = authOrRedirect();
      if (!auth) return [];

      try {
        const response = await fetch(
          `${API_BASE}/psy/client?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          showMessage("warning", "Sessão expirada. Faça login novamente para buscar pacientes.", {
            actionLabel: "Ir para login",
            onAction: () => navigate("/login", { replace: true }),
          });
          return [];
        }

        if (!response.ok) return [];

        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const q = normalizeText(search);
        const qDigits = onlyDigits(search);

        return list
          .filter((client) => {
            const textSearch = normalizeText(
              [client?.nome, client?.email, client?.telefone].filter(Boolean).join(" ")
            );
            const digitSearch = onlyDigits(client?.telefone);

            return (
              textSearch.includes(q) ||
              (qDigits && digitSearch.includes(qDigits))
            );
          })
          .slice(0, 12);
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
        setSearchError("Falha ao buscar pacientes.");
        return [];
      }
    },
    [authOrRedirect, navigate, showMessage]
  );

  useEffect(() => {
    if (skipNextSuggestRef.current) {
      skipNextSuggestRef.current = false;
      return;
    }

    const q = query.trim();

    if (
      clienteSelecionado?._id &&
      q &&
      q === String(clienteSelecionado?.nome || "")
    ) {
      setOpenSug(false);
      setSuggestions([]);
      return;
    }

    if (!q) {
      setSuggestions([]);
      setOpenSug(false);
      setLoadingSug(false);
      setSearchError("");
      return;
    }

    setLoadingSug(true);
    setSearchError("");

    const timeoutId = window.setTimeout(async () => {
      const results = await fetchClients(q);
      setSuggestions(results);
      setOpenSug(true);
      setLoadingSug(false);
    }, 280);

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const selectClient = (client) => {
    setClienteSelecionado(client);
    skipNextSuggestRef.current = true;
    setQuery(client?.nome || "");
    setOpenSug(false);
    setSuggestions([]);
    setErrors((prev) => ({ ...prev, cliente: "" }));
  };

  const clearClient = () => {
    setClienteSelecionado(null);
    setQuery("");
    setOpenSug(false);
    setSuggestions([]);
    setErrors((prev) => ({ ...prev, cliente: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!clienteSelecionado?._id) {
      nextErrors.cliente = "Selecione um paciente válido da lista.";
    }

    if (!dateISO) {
      nextErrors.data = "Informe a data do agendamento.";
    } else if (!parseISODate(dateISO)) {
      nextErrors.data = "Data inválida.";
    }

    if (!hora) {
      nextErrors.hora = "Informe o horário.";
    } else if (!parseTimeHHMM(hora)) {
      nextErrors.hora = "Horário inválido.";
    }

    const dateTime = buildLocalDateTime(dateISO, hora);

    if (!isEdit && dateTime && dateTime.getTime() < Date.now() - 5 * 60 * 1000) {
      nextErrors.hora = "Não é possível criar agendamento no passado.";
    }

    if (!String(valor || "").trim()) {
      nextErrors.valor = "Informe o valor da sessão.";
    } else {
      const money = parseMoneyBR(valor);
      if (!Number.isFinite(money) || money <= 0) {
        nextErrors.valor = "Informe um valor maior que zero.";
      }
    }

    if (!SESSION_TYPES.some((item) => item.value === tipoSessao)) {
      nextErrors.tipoSessao = "Tipo de sessão inválido.";
    }

    if (recorrencia !== "Nenhuma") {
      const repeat = Number.parseInt(repeatCount || "0", 10);

      if (!repeat || repeat < 1) {
        nextErrors.repeatCount = "Informe uma quantidade válida.";
      } else if (repeat > 60) {
        nextErrors.repeatCount = "Limite máximo: 60 repetições.";
      }
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showMessage("warning", "Revise os campos destacados antes de salvar.");
      focusFirstError();
      return false;
    }

    return true;
  };

  const buildBody = (dateTime) => {
    const dataYYYYMMDD = `${dateTime.getFullYear()}-${pad2(
      dateTime.getMonth() + 1
    )}-${pad2(dateTime.getDate())}`;

    const horaHHMM = `${pad2(dateTime.getHours())}:${pad2(dateTime.getMinutes())}`;
    const dataHoraISO = formatISOWithOffset(dateTime);
    const observacoesNormalizadas = normalizeMultiline(observacoes) || null;
    const valorNumber = parseMoneyBR(valor);

    const baseBody = {
      clienteId: clienteSelecionado._id,
      servico: tipoSessao,
      valorAgendamento: valorNumber,
      data: dataYYYYMMDD,
      hora: horaHHMM,
      dataHora: dataHoraISO,
      criarGoogleMeet:
        tipoSessao === "Online" &&
        criarGoogleMeet,
      observacoes: observacoesNormalizadas,
    };

    if (!isEdit) {
      return {
        ...baseBody,
        status: "pendente",
      };
    }

    // Na edição, envia apenas o que mudou.
    // Isso evita sobrescrever campos sem necessidade e deixa o PUT mais cirúrgico.
    const patch = {};

    const originalClienteId =
      editingData?.clienteId?._id || editingData?.clienteId || "";

    if (String(baseBody.clienteId) !== String(originalClienteId)) {
      patch.clienteId = baseBody.clienteId;
    }

    if (String(baseBody.servico) !== String(editingData?.servico || "")) {
      patch.servico = baseBody.servico;
    }

    const originalCriarGoogleMeet = Boolean(
      editingData?.googleMeet?.solicitado ||
      editingData?.googleMeet?.criado
    );

    if (
      Boolean(baseBody.criarGoogleMeet) !==
      originalCriarGoogleMeet
    ) {
      patch.criarGoogleMeet =
        Boolean(baseBody.criarGoogleMeet);
    }

    const originalValor = parseMoneyBR(
      editingData?.valorAgendamento ?? editingData?.valor ?? ""
    );

    if (
      Number.isFinite(valorNumber) &&
      Math.abs(valorNumber - originalValor) > 0.001
    ) {
      patch.valorAgendamento = baseBody.valorAgendamento;
    }

    const originalDate = new Date(editingData?.dataHora || editingData?.data || "");

    if (
      Number.isNaN(originalDate.getTime()) ||
      Math.abs(originalDate.getTime() - dateTime.getTime()) > 1000
    ) {
      patch.data = baseBody.data;
      patch.hora = baseBody.hora;
      patch.dataHora = baseBody.dataHora;
    }

    const originalObs = normalizeMultiline(editingData?.observacoes || "") || null;

    if (observacoesNormalizadas !== originalObs) {
      patch.observacoes = observacoesNormalizadas;
    }

    return patch;
  };

  const buildRecurrenceBody = () => {
    const agendamentos = recurrencePreview.map((dateTime) => buildBody(dateTime));
    const baseBody = agendamentos[0] || buildBody(buildLocalDateTime(dateISO, hora));

    return {
      ...baseBody,
      recorrencia,
      quantidade: agendamentos.length,
      totalSolicitado: agendamentos.length,
      agendamentos,
    };
  };

  const executarSalvamento = async (escopoRecorrencia = null, idsSelecionados = []) => {
    if (!validate()) return;

    const auth = authOrRedirect();
    if (!auth) return;

    const baseDateTime = buildLocalDateTime(dateISO, hora);

    if (!baseDateTime) {
      showMessage("warning", "Data ou horário inválido.");
      return;
    }

    const editBody = isEdit ? buildBody(baseDateTime) : null;

    if (isEdit && Object.keys(editBody || {}).length === 0) {
      showMessage("info", "Nenhuma alteração encontrada para atualizar.");
      return;
    }

    setSaving(true);

    try {
      let currentToken = auth.token;
      let response = null;
      let successTitle = "Agendamento salvo";
      let successText = "O novo compromisso foi criado com sucesso.";

      if (isEdit) {
        const body = {
          ...editBody,
          ...(isRecurringEdit && escopoRecorrencia
            ? { escopoRecorrencia, ...(escopoRecorrencia === "SELECIONADOS" ? { idsSelecionados } : {}) }
            : {}),
        };

        response = await fetch(
          `${API_BASE}/psy/schedule/${encodeURIComponent(editingData._id)}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${currentToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );

        successTitle = "Agendamento atualizado";
        successText =
          escopoRecorrencia === "TODOS_DA_SERIE"
            ? "Os compromissos futuros da série foram atualizados com sucesso."
            : escopoRecorrencia === "SELECIONADOS"
            ? "Os compromissos selecionados foram atualizados com sucesso."
            : "O compromisso foi atualizado com sucesso.";
      } else if (recorrencia !== "Nenhuma") {
        const body = buildRecurrenceBody();

        response = await fetch(
          `${API_BASE}/psy/schedule/recorrencia`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${currentToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );

        successTitle = "Recorrência criada";
        successText = `${body.agendamentos.length} compromisso(s) foram criados com sucesso.`;
      } else {
        const body = buildBody(baseDateTime);

        response = await fetch(
          `${API_BASE}/psy/schedule`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${currentToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );
      }

      const newToken = response.headers.get("x-new-token");

      if (newToken) {
        saveAuthToken(newToken);
        currentToken = newToken;
      }

      if (response.status === 401 || response.status === 403) {
        showMessage("warning", "Sessão expirada. Faça login novamente para salvar o agendamento.", {
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
          autoCloseMs: 0,
        });
        return;
      }

      if (!response.ok) {
        const payload = await readApiPayload(response);
        const details = getConflictDetails(payload);
        const presentation = getScheduleErrorPresentation(response, payload);

        showMessage(
          presentation.type,
          getApiMessage(payload, "Não foi possível salvar o agendamento."),
          {
            title: presentation.title,
            details,
            autoCloseMs: details.length ? 0 : 6500,
          }
        );
        return;
      }

      const payload = await readApiPayload(response);
      const totalCriado = Number(payload?.totalCriado || 0);
      const totalAtualizado = Number(payload?.totalAtualizado || 0);

      if (!isEdit && recorrencia !== "Nenhuma" && totalCriado > 0) {
        successText = `${totalCriado} compromisso(s) foram criados com sucesso.`;
      }

      if (
        isEdit &&
        escopoRecorrencia === "TODOS_DA_SERIE" &&
        totalAtualizado > 0
      ) {
        successText = `${totalAtualizado} compromisso(s) futuro(s) da série foram atualizados com sucesso.`;
      }

      showMessage("success", `${successText} Voltando para a agenda...`, {
        title: successTitle,
      });

      setSeriesScopeOpen(false);

      window.setTimeout(() => {
        navigate("/schedule", {
          replace: true,
          state: {
            refresh: true,
            notice: {
              type: "success",
              title: successTitle,
              message: successText,
            },
          },
        });
      }, 650);
    } catch (error) {
      console.error("Erro ao salvar agendamento:", error);
      showMessage("warning", "Falha de comunicação com a API. Verifique sua conexão e tente novamente.", {
        title: "Falha de comunicação",
      });
    } finally {
      setSaving(false);
    }
  };

  const abrirSelecaoDaSerie = async () => {
    const auth = authOrRedirect();
    if (!auth) return;
    setLoadingSeries(true);
    try {
      const response = await fetch(`${API_BASE}/psy/schedule`, {
        headers: { Authorization: `Bearer ${auth.token}`, Accept: "application/json" },
      });
      saveAuthToken(response.headers.get("x-new-token"));
      const data = await readApiPayload(response);
      if (!response.ok) throw new Error(getApiMessage(data));
      const appointments = Array.isArray(data) ? data :
        (Array.isArray(data?.agendamentos) ? data.agendamentos : Array.isArray(data?.data) ? data.data : []);
      const base = new Date(editingData?.dataHora || editingData?.data);
      const baseTime = base.getTime();
      const allowed = appointments.filter((item) => {
        const rid = item?.recorrenciaId || item?.recorrencia?.id;
        const status = String(item?.status || "").toLowerCase();
        const when = new Date(item?.dataHora || item?.data).getTime();
        return String(rid || "") === String(editingRecurrenceId) &&
          Number.isFinite(when) && when >= baseTime &&
          !["cancelado", "baixado", "expirado", "no_show"].includes(status);
      }).sort((a, b) => new Date(a.dataHora || a.data) - new Date(b.dataHora || b.data));
      if (!allowed.some((item) => String(item._id) === String(editingData._id))) {
        throw new Error("A ocorrência em edição não foi localizada entre os agendamentos abertos da série.");
      }
      setSeriesItems(allowed);
      setSelectedSeriesIds([String(editingData._id)]);
      setSeriesSelectionOpen(true);
    } catch (error) {
      showMessage("warning", error.message || "Não foi possível consultar a série.");
    } finally {
      setLoadingSeries(false);
    }
  };

  const salvarAgendamento = () => {
    if (!validate()) return;

    const baseDateTime = buildLocalDateTime(dateISO, hora);

    if (!baseDateTime) {
      showMessage("warning", "Data ou horário inválido.");
      return;
    }

    if (isEdit) {
      const body = buildBody(baseDateTime);

      if (Object.keys(body).length === 0) {
        showMessage("info", "Nenhuma alteração encontrada para atualizar.");
        return;
      }
    }

    if (isRecurringEdit) {
      setSeriesScopeOpen(true);
      return;
    }

    executarSalvamento();
  };

  const goBack = () => {
    if (saving) return;
    navigate("/schedule");
  };

  return (
    <div className="app-page" ref={pageRef}>
      <div className="app-bg" aria-hidden="true">
        <span className="app-bg__orb app-bg__orb--one" />
        <span className="app-bg__orb app-bg__orb--two" />
        <span className="app-bg__orb app-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="app-topbar">
        <button
          className="app-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="app-brand">
          <img src={Logo} alt="PsyAssist" className="app-brand__logo" />
          <div className="app-brand__text">
            <strong>{titulo}</strong>
            <span>Agendamentos • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="app-top-actions">
          <button
            className="app-btn app-btn--ghost app-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Voltar
          </button>

          <button
            className="app-btn app-btn--primary"
            type="button"
            onClick={salvarAgendamento}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  aria-hidden="true"
                  className="app-icon-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} aria-hidden="true" />
                Salvar
              </>
            )}
          </button>
        </div>
      </header>

      <main className="app-main">
        <section className="app-hero">
          <div>
            <span className="app-kicker">
              {isEdit ? "Atualização de agenda" : "Novo compromisso"}
            </span>
            <h1>{titulo}</h1>
            <p>
              Cadastre sessões com horário seguro, valor padronizado,
              recorrência controlada e seleção rápida de pacientes.
            </p>
          </div>

          <aside className="app-preview-card">
            <div className={`app-preview-card__avatar app-preview-card__avatar--${selectedType.tone}`}>
              {clienteSelecionado?._id ? getInitials(clienteSelecionado.nome) : selectedType.icon}
            </div>

            <div className="app-preview-card__content">
              <small>
                <Clock3 size={14} aria-hidden="true" />
                Prévia
              </small>
              <strong>{clienteSelecionado?.nome || "Paciente não selecionado"}</strong>
              <span>
                {selectedDateTime
                  ? `${formatDateBR(selectedDateTime)} às ${hora || "--:--"}`
                  : "Informe data e horário"}
              </span>
            </div>
          </aside>
        </section>

          <div
            className={`app-google-sync ${
              googleCalendarStatus.conectado
                ? "app-google-sync--connected"
                : "app-google-sync--disconnected"
            }`}
          >
            <div className="app-google-sync__icon" aria-hidden="true">
              G
            </div>

            <div className="app-google-sync__body">
              <strong>
                {googleCalendarStatus.carregando
                  ? "Consultando Google Agenda..."
                  : googleCalendarStatus.conectado
                    ? "Google Agenda conectado"
                    : "Google Agenda não conectado"}
              </strong>

              <span>
                {googleCalendarStatus.conectado
                  ? isEdit
                    ? "Ao salvar, o mesmo evento será atualizado automaticamente."
                    : "Ao salvar, este agendamento será enviado automaticamente para o Google."
                  : "O agendamento será salvo normalmente apenas no PsyAssist."}
              </span>
            </div>

            {!googleCalendarStatus.carregando &&
            !googleCalendarStatus.conectado ? (
              <button
                type="button"
                onClick={() => navigate("/integrations")}
              >
                <ExternalLink size={14} aria-hidden="true" />
                Conectar
              </button>
            ) : (
              <span className="app-google-sync__status">
                {googleCalendarStatus.carregando ? (
                  <LoaderCircle
                    size={14}
                    aria-hidden="true"
                    className="app-icon-spin"
                  />
                ) : (
                  <CheckCircle2 size={14} aria-hidden="true" />
                )}
                {googleCalendarStatus.carregando
                  ? "Consultando"
                  : "Ativo"}
              </span>
            )}
          </div>


        <section className="app-wrap">

          <section className="app-card">
            <div className="app-card__head">
              <div>
                <span className="app-card__eyebrow">
                  <CalendarDays size={14} aria-hidden="true" />
                  Agenda
                </span>
                <h2>Dados do agendamento</h2>
                <p>
                  Campos obrigatórios são validados antes do envio. Em recorrência,
                  o sistema cria uma sequência de horários a partir da data base.
                </p>
              </div>

              <div className="app-score">
                <small>Completude</small>
                <strong>{completion}%</strong>
                <div className="app-score__bar">
                  <span style={{ width: `${completion}%` }} />
                </div>
              </div>
            </div>

            <div className="app-grid">
              <Field
                label="Nome do paciente"
                required
                error={errors.cliente}
                hint={clienteSelecionado?._id ? "Selecionado" : "Autocomplete"}
                className="app-field--full"
              >
                <div className="app-autocomplete" ref={sugBoxRef}>
                  <div className="app-autocomplete__input">
                    <Search size={18} aria-hidden="true" />
                    <input
                      value={query}
                      onChange={(event) => {
                        setQuery(event.target.value);
                        setClienteSelecionado(null);
                        setErrors((prev) => ({ ...prev, cliente: "" }));
                      }}
                      onFocus={() => {
                        const q = query.trim();

                        if (
                          q &&
                          (!clienteSelecionado ||
                            q !== String(clienteSelecionado?.nome || "")) &&
                          suggestions.length
                        ) {
                          setOpenSug(true);
                        }
                      }}
                      placeholder="Digite para buscar paciente..."
                      autoComplete="off"
                    />

                    {query ? (
                      <button
                        type="button"
                        onClick={clearClient}
                        aria-label="Limpar paciente"
                      >
                        <X size={15} aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>

                  {clienteSelecionado?._id ? (
                    <div className="app-selected-client">
                      <span>{getInitials(clienteSelecionado.nome)}</span>
                      <div>
                        <strong>{clienteSelecionado.nome}</strong>
                        <small>
                          {formatPhoneBR(clienteSelecionado.telefone) ||
                            clienteSelecionado.email ||
                            "Paciente selecionado"}
                        </small>
                      </div>
                    </div>
                  ) : null}

                  {openSug ? (
                    <div className="app-sug">
                      {loadingSug ? (
                        <div className="app-sug__item app-sug__muted">
                          Buscando pacientes...
                        </div>
                      ) : searchError ? (
                        <div className="app-sug__item app-sug__muted">
                          {searchError}
                        </div>
                      ) : suggestions.length === 0 ? (
                        <div className="app-sug__item app-sug__muted">
                          Nenhum paciente encontrado.
                        </div>
                      ) : (
                        suggestions.map((client) => (
                          <button
                            key={client?._id}
                            type="button"
                            className="app-sug__item"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectClient(client)}
                          >
                            <div className="app-sug__avatar">
                              {getInitials(client?.nome)}
                            </div>
                            <div className="app-sug__content">
                              <strong>{client?.nome || "—"}</strong>
                              <small>
                                {formatPhoneBR(client?.telefone) ||
                                  client?.email ||
                                  "Sem contato informado"}
                              </small>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
              </Field>

              <Field label="Tipo de sessão" required error={errors.tipoSessao} className="app-field--full">
                <div className="app-choice-grid">
                  {SESSION_TYPES.map((option) => (
                    <SessionTypeCard
                      key={option.value}
                      option={option}
                      active={tipoSessao === option.value}
                      disabled={saving}
                      onSelect={(value) => {
                        setTipoSessao(value);
                        setErrors((prev) => ({ ...prev, tipoSessao: "" }));
                      }}
                    />
                  ))}
                </div>
              </Field>

              {tipoSessao === "Online" ? (
                <div className="app-meet-card app-field--full">
                  <div className="app-meet-card__icon" aria-hidden="true">
                    <Video size={22} />
                  </div>

                  <div className="app-meet-card__body">
                    <strong>Criar sala no Google Meet</strong>
                    <span>
                      Gere um link exclusivo e vincule-o automaticamente ao evento do Google Agenda.
                    </span>

                    {!googleCalendarStatus.carregando &&
                    !googleCalendarStatus.conectado ? (
                      <small>
                        Conecte o Google Agenda para habilitar esta opção.
                      </small>
                    ) : null}
                  </div>

                  <label className="app-switch">
                    <input
                      type="checkbox"
                      checked={criarGoogleMeet}
                      onChange={(event) =>
                        setCriarGoogleMeet(
                          event.target.checked
                        )
                      }
                      disabled={
                        saving ||
                        googleCalendarStatus.carregando ||
                        !googleCalendarStatus.conectado
                      }
                    />
                    <span aria-hidden="true" />
                  </label>
                </div>
              ) : null}

              <Field label="Data" required error={errors.data}>
                <input
                  type="date"
                  className="app-input"
                  value={dateISO}
                  onChange={(event) => {
                    setDateISO(event.target.value);
                    setErrors((prev) => ({ ...prev, data: "" }));
                  }}
                />
              </Field>

              <Field label="Horário" required error={errors.hora} hint="Intervalo de 5 min">
                <input
                  type="time"
                  className="app-input"
                  value={hora}
                  onChange={(event) => {
                    setHora(event.target.value);
                    setErrors((prev) => ({ ...prev, hora: "" }));
                  }}
                  step={300}
                />
              </Field>

              <Field label="Valor da sessão" required error={errors.valor} hint="R$">
                <input
                  className="app-input"
                  value={valor}
                  onChange={(event) => {
                    setValor(formatMoneyFromDigits(event.target.value));
                    setErrors((prev) => ({ ...prev, valor: "" }));
                  }}
                  placeholder="150,00"
                  inputMode="numeric"
                />
              </Field>

              <Field label="Resumo financeiro" hint="Calculado">
                <div className="app-money-box">
                  <span>Valor informado</span>
                  <strong>{formatCurrencyBR(valor)}</strong>
                </div>
              </Field>

              <Field label="Observações" className="app-field--full">
                <textarea
                  className="app-textarea"
                  value={observacoes}
                  onChange={(event) => setObservacoes(event.target.value)}
                  onBlur={(event) =>
                    setObservacoes(normalizeMultiline(event.target.value))
                  }
                  placeholder="Digite informações adicionais sobre o agendamento..."
                  rows={4}
                />
              </Field>

              <Field
                label="Recorrência"
                hint={isEdit ? (isRecurringEdit ? "Este agendamento pertence a uma série" : "Desabilitada na edição") : "Opcional"}
                className="app-field--full"
              >
                <div className="app-recur-grid">
                  {RECURRENCE_OPTIONS.map((option) => (
                    <RecurrenceCard
                      key={option.value}
                      option={option}
                      active={recorrencia === option.value}
                      disabled={saving || isEdit}
                      onSelect={(value) => {
                        setRecorrencia(value);
                        setErrors((prev) => ({ ...prev, repeatCount: "" }));
                      }}
                    />
                  ))}
                </div>
              </Field>

              {recorrencia !== "Nenhuma" ? (
                <Field
                  label="Quantidade de repetições"
                  required
                  error={errors.repeatCount}
                  hint="máximo 60"
                >
                  <input
                    className="app-input"
                    value={repeatCount}
                    onChange={(event) => {
                      setRepeatCount(onlyDigits(event.target.value).slice(0, 2));
                      setErrors((prev) => ({ ...prev, repeatCount: "" }));
                    }}
                    placeholder="Ex.: 4"
                    inputMode="numeric"
                    disabled={saving || isEdit}
                  />
                </Field>
              ) : null}

              {recurrencePreview.length > 0 ? (
                <div className="app-preview-list">
                  <div className="app-preview-list__head">
                    <strong>
                      <Repeat2 size={15} aria-hidden="true" />
                      {recorrencia === "Nenhuma"
                        ? "Horário que será criado"
                        : "Prévia dos horários"}
                    </strong>
                    <span>{recurrencePreview.length} item(ns)</span>
                  </div>

                  <div className="app-preview-list__items">
                    {recurrencePreview.slice(0, 8).map((date, index) => (
                      <span key={`${date.toISOString()}-${index}`}>
                        {formatDateBR(date)} • {pad2(date.getHours())}:{pad2(date.getMinutes())}
                      </span>
                    ))}

                    {recurrencePreview.length > 8 ? (
                      <span>+ {recurrencePreview.length - 8} horário(s)</span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="app-actions" aria-label="Ações do agendamento">
                <button
                  className="app-btn app-btn--ghost"
                  type="button"
                  onClick={goBack}
                  disabled={saving}
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                  Voltar
                </button>

                <button
                  className="app-btn app-btn--primary"
                  type="button"
                  onClick={salvarAgendamento}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <LoaderCircle
                        size={17}
                        aria-hidden="true"
                        className="app-icon-spin"
                      />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save size={17} aria-hidden="true" />
                      {isEdit
                        ? "Atualizar agendamento"
                        : "Salvar agendamento"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        </section>
      </main>

      {seriesScopeOpen ? (
        <div
          className="app-series-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-series-modal-title"
        >
          <div className="app-series-modal">
            <div className="app-series-modal__icon" aria-hidden="true">
              <Repeat2 size={24} />
            </div>

            <div className="app-series-modal__content">
              <span className="app-series-modal__eyebrow">Série recorrente</span>
              <h2 id="app-series-modal-title">Onde deseja aplicar esta alteração?</h2>
              <p>
                Este compromisso pertence a uma série recorrente. Escolha se a
                mudança deve valer somente para este horário ou para todos os
                compromissos futuros e abertos da mesma série.
              </p>

              <div className="app-series-modal__options">
                <button
                  type="button"
                  className="app-series-option"
                  onClick={() => executarSalvamento("SOMENTE_ESTE")}
                  disabled={saving}
                >
                  <span className="app-series-option__icon">
                    <CalendarDays size={20} aria-hidden="true" />
                  </span>
                  <span>
                    <strong>Somente este</strong>
                    <small>
                      Altera apenas o compromisso selecionado e mantém os demais
                      horários da série como estão.
                    </small>
                  </span>
                </button>

                <button
                  type="button"
                  className="app-series-option app-series-option--primary"
                  onClick={() => executarSalvamento("TODOS_DA_SERIE")}
                  disabled={saving}
                >
                  <span className="app-series-option__icon">
                    <Repeat2 size={20} aria-hidden="true" />
                  </span>
                  <span>
                    <strong>Todos da série</strong>
                    <small>
                      Aplica a mudança a este e aos próximos compromissos abertos.
                      O histórico já encerrado não será alterado.
                    </small>
                  </span>
                </button>
                <button
                  type="button"
                  className="app-series-option"
                  onClick={abrirSelecaoDaSerie}
                  disabled={saving || loadingSeries}
                >
                  <span className="app-series-option__icon"><CalendarDays size={20} /></span>
                  <span><strong>Escolher agendamentos</strong><small>Selecione individualmente os horários futuros que serão alterados.</small></span>
                </button>
              </div>
              {seriesSelectionOpen && (
                <div style={{ marginTop: 16 }}>
                  <p>Selecione os agendamentos da série. O agendamento em edição permanece selecionado.</p>
                  <div style={{ maxHeight: 240, overflowY: "auto", display: "grid", gap: 8 }}>
                    {seriesItems.map((item) => {
                      const id = String(item._id);
                      const pinned = id === String(editingData._id);
                      const when = new Date(item.dataHora || item.data);
                      return <label key={id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input type="checkbox" checked={selectedSeriesIds.includes(id)} disabled={saving || pinned}
                          onChange={(event) => setSelectedSeriesIds((old) => event.target.checked ? [...old, id] : old.filter((value) => value !== id))} />
                        <span>{when.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })} — {item.status || "Agendado"}{pinned ? " (atual)" : ""}</span>
                      </label>;
                    })}
                  </div>
                  <button type="button" className="app-btn app-btn--primary" style={{ marginTop: 12 }}
                    disabled={saving || !selectedSeriesIds.length}
                    onClick={() => executarSalvamento("SELECIONADOS", selectedSeriesIds)}>
                    Aplicar aos {selectedSeriesIds.length} selecionados
                  </button>
                </div>
              )}
            </div>

            <div className="app-series-modal__actions">
              <button
                type="button"
                className="app-btn app-btn--ghost"
                onClick={() => setSeriesScopeOpen(false)}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
